import { useEffect, useState, useMemo, useRef } from 'react';
import {
  Card,
  CardBody,
  Input,
  Chip,
  ScrollShadow,
} from '@nextui-org/react';
import { Icon } from '@iconify/react';
import {
  getAgentContactsWithUsers,
  getAgentMessagesByContact,
  AgentContact,
  AgentMessage,
} from 'common/src/api/agent-messages';
import { EmptyState, LoadingState } from '../../../components/ui';

const formatTime = (dateStr: string) => {
  const d = new Date(dateStr);
  return d.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' });
};

const formatDate = (dateStr: string) => {
  const d = new Date(dateStr);
  return d.toLocaleDateString('es-CL', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const formatDateSeparator = (dateStr: string) => {
  const d = new Date(dateStr);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  if (d.toDateString() === today.toDateString()) return 'Hoy';
  if (d.toDateString() === yesterday.toDateString()) return 'Ayer';
  return d.toLocaleDateString('es-CL', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
};

const getContactName = (contact: AgentContact) => {
  const first = contact.user?.first_name || '';
  const last = contact.user?.last_name || '';
  const full = `${first} ${last}`.trim();
  return full || contact.phone_number;
};

const getExporterName = (contact: AgentContact) => {
  return contact.user?.exporter?.name || '';
};

const SuperadminChat = () => {
  const [contacts, setContacts] = useState<AgentContact[]>([]);
  const [messages, setMessages] = useState<AgentMessage[]>([]);
  const [selectedContact, setSelectedContact] = useState<AgentContact | null>(
    null
  );
  const [loadingContacts, setLoadingContacts] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [search, setSearch] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchContacts = async () => {
      try {
        const data = await getAgentContactsWithUsers();
        setContacts(data);
      } catch (error) {
        console.error('Error fetching contacts:', error);
      } finally {
        setLoadingContacts(false);
      }
    };
    fetchContacts();
  }, []);

  useEffect(() => {
    if (!selectedContact) {
      setMessages([]);
      return;
    }
    const fetchMessages = async () => {
      setLoadingMessages(true);
      try {
        const data = await getAgentMessagesByContact(selectedContact.id);
        setMessages(data);
      } catch (error) {
        console.error('Error fetching messages:', error);
      } finally {
        setLoadingMessages(false);
      }
    };
    fetchMessages();
  }, [selectedContact]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const filteredContacts = useMemo(() => {
    if (!search) return contacts;
    const lower = search.toLowerCase();
    return contacts.filter((c) => {
      const name = getContactName(c).toLowerCase();
      const exporter = getExporterName(c).toLowerCase();
      const phone = c.phone_number;
      return (
        name.includes(lower) ||
        exporter.includes(lower) ||
        phone.includes(lower)
      );
    });
  }, [contacts, search]);

  const messageDays = useMemo(() => {
    const groups: { date: string; messages: AgentMessage[] }[] = [];
    let currentDate = '';
    messages.forEach((msg) => {
      const msgDate = new Date(msg.created_at).toDateString();
      if (msgDate !== currentDate) {
        currentDate = msgDate;
        groups.push({ date: msg.created_at, messages: [msg] });
      } else {
        groups[groups.length - 1].messages.push(msg);
      }
    });
    return groups;
  }, [messages]);

  return (
    <div className='flex w-full h-screen overflow-hidden'>
      {/* Left panel - Contacts */}
      <div className='w-80 min-w-[320px] border-r border-divider flex flex-col bg-content1'>
        <div className='p-4 border-b border-divider'>
          <h2 className='text-lg font-semibold mb-3'>Asistente IA</h2>
          <Input
            placeholder='Buscar por nombre, teléfono...'
            value={search}
            onValueChange={setSearch}
            startContent={
              <Icon icon='tabler:search' className='text-default-400' />
            }
            size='sm'
            variant='bordered'
          />
        </div>

        <ScrollShadow className='flex-1 overflow-y-auto'>
          {loadingContacts ? (
            <LoadingState />
          ) : filteredContacts.length === 0 ? (
            <div className='p-4 text-center text-default-400 text-sm'>
              No se encontraron contactos
            </div>
          ) : (
            filteredContacts.map((contact) => (
              <div
                key={contact.id}
                onClick={() => setSelectedContact(contact)}
                className={`flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-default-100 transition-colors border-b border-divider ${
                  selectedContact?.id === contact.id ? 'bg-default-100' : ''
                }`}
              >
                <div className='w-10 h-10 rounded-full bg-default-200 flex items-center justify-center flex-shrink-0'>
                  <Icon
                    icon='tabler:user'
                    className='text-default-500'
                    width={20}
                  />
                </div>
                <div className='flex-1 min-w-0'>
                  <div className='flex items-center gap-2'>
                    <span className='font-medium text-sm truncate'>
                      {getContactName(contact)}
                    </span>
                    <Chip
                      size='sm'
                      variant='flat'
                      color={contact.is_active ? 'success' : 'default'}
                      className='h-5 text-[10px]'
                    >
                      {contact.is_active ? 'Activo' : 'Inactivo'}
                    </Chip>
                  </div>
                  <div className='text-xs text-default-400 truncate'>
                    {getExporterName(contact)}
                  </div>
                  <div className='text-xs text-default-400'>
                    {contact.phone_number}
                  </div>
                </div>
              </div>
            ))
          )}
        </ScrollShadow>
      </div>

      {/* Right panel - Messages */}
      <div className='flex-1 flex flex-col bg-default-50'>
        {!selectedContact ? (
          <div className='flex-1 flex flex-col items-center justify-center text-default-400'>
            <Icon icon='tabler:message-chatbot' width={64} className='mb-4' />
            <p className='text-lg'>Selecciona un contacto para ver los mensajes</p>
          </div>
        ) : (
          <>
            {/* Chat header */}
            <div className='px-6 py-4 border-b border-divider bg-content1 flex items-center gap-3'>
              <div className='w-10 h-10 rounded-full bg-default-200 flex items-center justify-center'>
                <Icon
                  icon='tabler:user'
                  className='text-default-500'
                  width={20}
                />
              </div>
              <div>
                <div className='font-semibold'>
                  {getContactName(selectedContact)}
                </div>
                <div className='text-xs text-default-400'>
                  {getExporterName(selectedContact)}
                  {getExporterName(selectedContact) && ' · '}
                  {selectedContact.phone_number}
                </div>
              </div>
            </div>

            {/* Messages area */}
            <ScrollShadow className='flex-1 overflow-y-auto px-6 py-4'>
              {loadingMessages ? (
                <LoadingState />
              ) : messages.length === 0 ? (
                <div className='flex h-full items-center justify-center'>
                  <EmptyState icon='tabler:message-off' title='No hay mensajes' />
                </div>
              ) : (
                messageDays.map((group, gi) => (
                  <div key={gi}>
                    {/* Date separator */}
                    <div className='flex items-center justify-center my-4'>
                      <Chip size='sm' variant='flat' className='text-xs'>
                        {formatDateSeparator(group.date)}
                      </Chip>
                    </div>

                    {group.messages.map((msg) => {
                      const isUser = msg.role === 'user';
                      return (
                        <div
                          key={msg.id}
                          className='flex mb-3'
                          style={{ justifyContent: isUser ? 'flex-end' : 'flex-start' }}
                        >
                          <div
                            className='rounded-2xl px-4 py-2'
                            style={{
                              maxWidth: '70%',
                              backgroundColor: isUser ? 'var(--brand-700)' : 'white',
                              color: isUser ? 'white' : 'inherit',
                              border: isUser ? 'none' : '1px solid #e5e7eb',
                              borderBottomRightRadius: isUser ? '4px' : '16px',
                              borderBottomLeftRadius: isUser ? '16px' : '4px',
                            }}
                          >
                            <p className='text-sm whitespace-pre-wrap break-words'>
                              {msg.content || '(sin contenido)'}
                            </p>
                            <div className='flex items-center justify-end gap-1.5 mt-1'>
                              {msg.channel === 'web' && (
                                <span
                                  className='text-[9px] px-1 py-0.5 rounded'
                                  style={{
                                    backgroundColor: isUser ? 'rgba(255,255,255,0.2)' : '#f0f0f0',
                                    color: isUser ? 'rgba(255,255,255,0.8)' : '#888',
                                  }}
                                >
                                  WEB {msg.user_id ? `#${msg.user_id}` : ''}
                                </span>
                              )}
                              <p
                                className='text-[10px]'
                                style={{ opacity: isUser ? 0.6 : 0.4 }}
                              >
                                {formatTime(msg.created_at)}
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ))
              )}
              <div ref={messagesEndRef} />
            </ScrollShadow>
          </>
        )}
      </div>
    </div>
  );
};

export default SuperadminChat;
